import Header from "../../../shared/app-shell/Header";
import Sidebar from "../../../shared/app-shell/Sidebar";
import CargillLogoNight from "../../../shared/assets/CargillLogoNight.svg";
import { themeAtom } from "../../../shared/atoms";
import VesselEvaluationFilters from "../components/VesselEvaluationFilters";
import VesselEvaluationPagination from "../components/VesselEvaluationPagination";
import VesselEvaluationTable from "../components/VesselEvaluationTable";
import {
  EMPTY_VESSEL_EVALUATION_FILTERS,
  type VesselEvaluationFilterKey,
  type VesselEvaluationFilters as VesselEvaluationFilterValues,
  type VesselEvaluationSort,
} from "../models/vessel-evaluation.models";
import { useVesselEvaluationDashboard } from "../state/useVesselEvaluationDashboard";
import {
  formatVesselEvaluationSnapshotTime,
  groupVesselEvaluationsByVesselName,
} from "../utils/vessel-evaluation-selectors.utils";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const INITIAL_VESSEL_EVALUATION_SORT: VesselEvaluationSort = {
  key: "vesselName",
  direction: "ascending",
};
const VESSEL_EVALUATION_PAGE_SIZE = 50;

// Recreates the PBIP page while leaving data truth with the Snowflake-backed API whenever mock mode is disabled.
const VesselEvaluationFollower = () => {
  const theme = useAtomValue(themeAtom);
  const [filters, setFilters] = useState<VesselEvaluationFilterValues>(EMPTY_VESSEL_EVALUATION_FILTERS);
  const [sort, setSort] = useState<VesselEvaluationSort>(INITIAL_VESSEL_EVALUATION_SORT);
  const [offset, setOffset] = useState(0);
  const [filterResetRevision, setFilterResetRevision] = useState(0);
  const dashboardRequest = useMemo(() => ({
    filters,
    sort,
    offset,
    limit: VESSEL_EVALUATION_PAGE_SIZE,
  }), [filters, offset, sort]);
  const { dashboard, error, loading, mockEnabled, refreshDashboard } = useVesselEvaluationDashboard(dashboardRequest);
  const previousMockEnabled = useRef(mockEnabled);

  // Clears selections that may not exist in the newly selected source while preserving them across ordinary refreshes.
  useEffect(() => {
    if (previousMockEnabled.current === mockEnabled) {
      return;
    }

    previousMockEnabled.current = mockEnabled;
    setFilters(EMPTY_VESSEL_EVALUATION_FILTERS);
    setSort(INITIAL_VESSEL_EVALUATION_SORT);
    setOffset(0);
    setFilterResetRevision((current) => current + 1);
  }, [mockEnabled]);

  const visibleVessels = useMemo(
    () => groupVesselEvaluationsByVesselName(dashboard?.vessels ?? []),
    [dashboard?.vessels],
  );
  const evaluatedCount = dashboard?.totalVessels ?? 0;

  const setFilterValues = (key: VesselEvaluationFilterKey, values: string[]) => {
    setFilters((current) => ({ ...current, [key]: values }));
    setOffset(0);
  };

  const clearFilters = () => {
    setFilters(EMPTY_VESSEL_EVALUATION_FILTERS);
    setOffset(0);
    setFilterResetRevision((current) => current + 1);
  };

  const updateSort = (key: VesselEvaluationSort["key"]) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "ascending"
        ? "descending"
        : "ascending",
    }));
    setOffset(0);
  };

  const refreshedAt = formatVesselEvaluationSnapshotTime(dashboard?.refreshedAt ?? null);

  return (
    <div className="flex h-screen" data-test="vessel-evaluation-page">
      <Sidebar />
      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col overflow-hidden",
          theme.home.tailwind.backgound,
          theme.home.tailwind.textDefault,
        )}
      >
        <Header />
        <main className="flex-1 overflow-y-auto px-4 pb-6 pt-3 sm:px-6">
          <div className="mx-auto max-w-[1900px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <header className="grid items-center gap-3 bg-[#064c87] px-4 py-2.5 text-white xl:grid-cols-[auto_minmax(260px,1fr)_auto]">
              <img
                src={CargillLogoNight}
                alt="Cargill"
                className="h-8 w-auto"
                data-test="vessel-evaluation-cargill-logo"
              />
              <h1 className="text-xl font-semibold sm:text-2xl xl:text-center">Vessel Performance Summary</h1>
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-lg font-semibold" data-test="vessel-evaluation-count">
                  Vessels Evaluated : {evaluatedCount.toLocaleString()}
                </p>
                <span
                  className={twMerge(
                    "rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide",
                    mockEnabled ? "bg-amber-100 text-amber-800" : "bg-white/15 text-white",
                  )}
                  data-test="vessel-evaluation-source-badge"
                >
                  {mockEnabled ? "Modeled mock data" : "Snowflake API"}
                </span>
                <button
                  type="button"
                  onClick={refreshDashboard}
                  disabled={loading}
                  className="rounded border border-white/60 p-2 hover:bg-white/10 disabled:opacity-60"
                  aria-label="Refresh Vessel Evaluation"
                  data-test="vessel-evaluation-refresh"
                >
                  <ArrowPathIcon className={twMerge("h-4 w-4", loading && "animate-spin")} />
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="border border-white bg-[#f76761] px-6 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#e65954]"
                  data-test="vessel-evaluation-clear-filters"
                >
                  Clear Filters
                </button>
              </div>
            </header>

            <VesselEvaluationFilters
              filters={filters}
              filterOptions={dashboard?.filterOptions ?? EMPTY_VESSEL_EVALUATION_FILTERS}
              resetRevision={filterResetRevision}
              onChange={setFilterValues}
            />

            {error && (
              <div
                className="border-y border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
                role="alert"
                data-test="vessel-evaluation-error"
              >
                {error} Enable the sidebar mock-data toggle to review the page without Snowflake.
              </div>
            )}

            {loading && !dashboard ? (
              <div className="px-4 py-16 text-center text-sm text-gray-500" data-test="vessel-evaluation-loading">
                Loading Vessel Evaluation...
              </div>
            ) : dashboard ? (
              <VesselEvaluationTable
                vessels={visibleVessels}
                sort={sort}
                onSort={updateSort}
              />
            ) : null}

            <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-2 text-[10px] text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
              <span>{visibleVessels.length.toLocaleString()} rows loaded on this page</span>
              <VesselEvaluationPagination
                limit={dashboard?.limit ?? VESSEL_EVALUATION_PAGE_SIZE}
                loading={loading}
                offset={dashboard?.offset ?? offset}
                onOffsetChange={setOffset}
                total={dashboard?.totalVessels ?? 0}
              />
              <span data-test="vessel-evaluation-refreshed-at">Snapshot loaded: {refreshedAt}</span>
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
};

export default VesselEvaluationFollower;
